import React, { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { EXTRA_QUOTE_PRESETS } from "@/lib/billingCheckout";

/**
 * How many extra quotes — the one selector both the extra quote card and the
 * out-of-quotes card use (Jorge, 1b).
 *
 * Three buttons that look like buttons (+1 / +5 / +10, the pressed one filled
 * yellow) and, apart from them, a free field labelled "Other amount" that
 * starts empty. Typing in the field releases the buttons; pressing a button
 * clears the field. `onChange` gets a whole number between 1 and `max` (what
 * the checkout clamps to), or null while the field holds nothing usable, so
 * the caller can disable its buy button rather than guess.
 *
 * One row at 390 px: every column is `minmax(0, …)`, so the row can only be as
 * wide as its container — smoke_layout rule 9 keeps it that way.
 *
 * `fieldBelowOnSmallPhones` is for Dany's chat only, the narrowest of the
 * three surfaces (Jorge, 1b-a): measured in Chrome its buttons are 55 px wide
 * at 390 and 48 at 360, but 40 at 320, under a 44 px target. Below 360 px the
 * "Other amount" field there moves to a row of its own and the three buttons
 * share the full width; at 360 and up it is the same single row as anywhere.
 */
const STACKED = {
  grid: "max-[359px]:grid-cols-3",
  field: "max-[359px]:col-span-3 max-[359px]:row-start-3",
  input: "max-[359px]:col-span-3 max-[359px]:row-start-4",
};

export default function QuantitySelector({
  value, onChange, max = null, testId = "quantity", fieldBelowOnSmallPhones = false,
}) {
  const { t } = useTranslation();
  const id = useId();
  // No cap known: the server clamps at checkout all the same.
  const cap = max == null ? Infinity : Math.max(1, Number(max) || 1);
  const presets = EXTRA_QUOTE_PRESETS.filter((n) => n <= cap);
  const [custom, setCustom] = useState("");
  const stack = (part) => (fieldBelowOnSmallPhones ? ` ${STACKED[part]}` : "");

  const pick = (n) => {
    setCustom("");
    onChange(n);
  };
  const type = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 4);
    setCustom(digits);
    const n = parseInt(digits, 10);
    onChange(Number.isFinite(n) && n >= 1 ? Math.min(cap, n) : null);
  };

  return (
    <div
      role="group"
      aria-labelledby={`${id}-label`}
      className={`grid grid-cols-[repeat(3,minmax(0,1fr))_minmax(0,1.6fr)] gap-x-2 gap-y-1 items-end${stack("grid")}`}
      data-testid={testId}
    >
      <span id={`${id}-label`} className="col-span-3 text-xs font-display font-semibold text-worker">
        {t("extraQuote.quantity")}
      </span>
      <label htmlFor={`${id}-other`}
             className={`text-xs font-display font-semibold text-worker truncate${stack("field")}`}>
        {t("extraQuote.otherAmount")}
      </label>
      {presets.map((n) => {
        const pressed = custom === "" && value === n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => pick(n)}
            aria-pressed={pressed}
            className={`h-11 min-w-0 rounded-sm border-[1.5px] font-mono font-bold text-base transition-colors ${
              pressed
                ? "border-worker bg-hive text-worker"
                : "border-sand bg-cream text-worker hover:border-hive"
            }`}
            data-testid={`${testId}-preset-${n}`}
          >
            +{n}
          </button>
        );
      })}
      <input
        id={`${id}-other`}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={custom}
        onChange={(e) => type(e.target.value)}
        className={`h-11 w-full min-w-0 input-base text-center font-mono font-bold text-base${stack("input")}`}
        data-testid={`${testId}-other`}
      />
    </div>
  );
}
